import React from "react";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DataTable from "examples/Tables/DataTable";
import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";
import { Grid, IconButton, Tooltip, TextField, Tabs, Tab } from "@mui/material";
import MDAutocomplete from "components/MDAutocomplete";
import { useEffect, useState, useContext, useCallback } from "react";
import DefaultCell from "examples/Tables/components/DefaultCell";
import moment from "moment";
import { TABLE_FILTERS } from "utils/constants";
import {
  getFiltersFromCache,
  saveFiltersToCache,
  getSortByFromCache,
  saveSortByToCache,
} from "utils/cacheManager";
import { Visibility, CallMade, CallReceived } from "@mui/icons-material";
import DraggableDialog from "components/draggagle-dialog";
import ViewWalletTransactionForm from "./ViewWalletTransactionForm";
import { getTenants } from "lib/Api/Tenants";
import { getCardTransactions, getWalletTransactions } from "lib/Api/AllTransactions";
import { LoginFormContext } from "context/loginFormCtx";
import debounce from "lodash/debounce";

const cardTransactionTypes = [
  { label: "Add On", value: 1 },
  { label: "Plan purchase", value: 3 },
  { label: "Wallet Balance", value: 2 },
];

function WalletTransaction() {
  const { setNotification } = useContext(LoginFormContext);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 for Wallet, 1 for Card

  // Create a debounced version of getWalletTransactionData
  const debouncedGetData = useCallback(
    debounce((tenant_name) => {
      getWalletTransactionData(tenant_name);
    }, 500),
    []
  );

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenViewDialog(true);
  };

  const fetchTenants = async () => {
    try {
      setIsLoading(true);
      const response = await getTenants({
        // page: 1,
        // pageSize: 1000,
        filters: [],
        sortBy: [{ id: "company_name", value: "asc" }],
      });

      if (response?.status === 200) {
        const options = response.data?.data?.tenants?.map((tenant) => ({
          ...tenant,
          value: tenant.db_schema,
          label: tenant.company_name,
        }));
        setTenantOptions(options);

        // Automatically select the first tenant if options exist
        if (options.length > 0 && !selectedTenant) {
          setSelectedTenant(options[0]);
          getWalletTransactionData(options[0].value);
          const tempColumns = [...dataTableColumns];

          const columnIndex = tempColumns?.findIndex((obj) => obj?.accessor === "user_id");

          tempColumns[columnIndex].filterOptions = options[0]?.tenantUsers?.map(({ user }) => ({
            label: `${user?.first_name || ""} ${user?.last_name}`,
            value: user?.id,
          }));

          setDataTableColumns(tempColumns);
        }
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "error",
        title: "Error",
        content: "Failed to fetch tenants",
        open: true,
      });
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const walletTransactionColumns = [
    {
      Header: "Transaction Type",
      accessor: "transaction_type",
      filterInputType: "select",
      filterOptions: [
        { label: "Credit", value: 1 },
        { label: "Debit", value: 2 },
      ],
      sort: true,
      Cell: ({ value }) => {
        return (
          <MDBox display="flex" alignItems="center">
            <MDBox
              width="1.5rem"
              height="1.5rem"
              mr={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                backgroundColor: value === 1 ? "#E9F7EF" : "#FDEDEC",
                borderRadius: "50%",
              }}
            >
              {value === 1 ? (
                <CallReceived sx={{ fontSize: "1rem", color: "#4CAF50" }} />
              ) : (
                <CallMade sx={{ fontSize: "1rem", color: "#F44335" }} />
              )}
            </MDBox>
            <MDTypography variant="caption" fontWeight="medium">
              {value === 1 ? "Credit" : "Debit"}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      Header: "Transaction ID",
      accessor: "transaction_id",
      filterInputType: "text",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value || "-"} />;
      },
    },
    {
      Header: "Amount",
      accessor: "amount",
      filterInputType: "number",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value ? `$${parseFloat(value).toFixed(2)}` : "-"} />;
      },
    },
    {
      Header: "Actual Amount",
      accessor: "actual_cost",
      filterInputType: "number",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value ? `$${parseFloat(value).toFixed(4)}` : "-"} />;
      },
    },
    {
      Header: "Module",
      accessor: "transaction_module",
      filterInputType: "text",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value || "-"} />;
      },
    },
    {
      Header: "Card Number",
      accessor: "card_number",
      filterInputType: "number",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value ? `****${value}` : "-"} />;
      },
    },
    {
      Header: "User",
      accessor: "user_id",
      filterInputType: "select",
      filterOptions: [],
      sort: true,
      Cell: ({ row: { original } }) => {
        return (
          <DefaultCell
            value={`${original?.user?.first_name || ""} ${original?.user?.last_name || ""}`}
          />
        );
      },
    },
    {
      Header: "Created At",
      accessor: "created_at",
      filterInputType: "date",
      disableFuture: true,
      sort: true,
      Cell: ({ value }) => <DefaultCell value={value ? moment(value).format("MM/DD/YYYY") : "-"} />,
    },
    {
      Header: "Action",
      accessor: "actions",
      width: "3%",
      sort: false,
      Cell: ({ row }) => {
        return (
          <MDBox>
            <Tooltip title="View Transaction" placement="top">
              <IconButton
                onClick={() => {
                  handleViewTransaction({
                    ...(row?.original || {}),
                    transaction_type: row?.original?.transaction_type === 1 ? "Credit" : "Debit",
                  });
                }}
              >
                <Visibility />
              </IconButton>
            </Tooltip>
          </MDBox>
        );
      },
    },
  ];

  const cardTransactionColumns = [
    {
      Header: "Transaction Type",
      accessor: "transaction_type",
      filterInputType: "select",
      filterOptions: cardTransactionTypes,
      sort: true,
      Cell: ({ value }) => {
        return (
          <DefaultCell
            value={cardTransactionTypes?.find((type) => type?.value === value)?.label || "-"}
          />
        );
      },
    },
    {
      Header: "Transaction ID",
      accessor: "transaction_payment_intent_id",
      filterInputType: "text",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value || "-"} />;
      },
    },
    {
      Header: "Amount",
      accessor: "amount",
      filterInputType: "number",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value ? `$${parseFloat(value).toFixed(2)}` : "-"} />;
      },
    },
    {
      Header: "Card Number",
      accessor: "card_last4",
      filterInputType: "number",
      sort: true,
      Cell: ({ value }) => {
        return <DefaultCell value={value ? `****${value}` : "-"} />;
      },
    },
    {
      Header: "User",
      accessor: "user_id",
      filterInputType: "select",
      filterOptions: [],
      sort: true,
      Cell: ({ row: { original } }) => {
        return (
          <DefaultCell
            value={`${original?.user?.first_name || ""} ${original?.user?.last_name || ""}`}
          />
        );
      },
    },
    {
      Header: "Created At",
      accessor: "created_at",
      filterInputType: "date",
      disableFuture: true,
      sort: true,
      Cell: ({ value }) => <DefaultCell value={value ? moment(value).format("MM/DD/YYYY") : "-"} />,
    },
    {
      Header: "Action",
      accessor: "actions",
      width: "3%",
      sort: false,
      Cell: ({ row }) => {
        return (
          <MDBox>
            <Tooltip title="View Transaction" placement="top">
              <IconButton
                onClick={() => {
                  const obj = {
                    transaction_id: row?.original?.transaction_payment_intent_id,
                    transaction_type:
                      cardTransactionTypes?.find(
                        (type) => type?.value === row?.original?.transaction_type
                      )?.label || "-",
                    amount: row?.original?.amount,
                    card_number: row?.original?.card_last4,
                    created_at: row?.original?.created_at,
                    user: row?.original?.user,
                  };
                  handleViewTransaction(obj);
                }}
              >
                <Visibility />
              </IconButton>
            </Tooltip>
          </MDBox>
        );
      },
    },
  ];

  const [dataTableColumns, setDataTableColumns] = useState(walletTransactionColumns);

  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [tableData, setTableData] = useState({
    columns: dataTableColumns,
    rows: [],
  });
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(
    getFiltersFromCache(TABLE_FILTERS.WALLET_TRANSACTIONS) || []
  );
  const [sortBy, setSortBy] = useState(
    getSortByFromCache(TABLE_FILTERS.WALLET_TRANSACTIONS) || [{ id: "created_at", desc: true }]
  );

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
    setPage(0);
  };

  const handleFiltersChange = (filterObject) => {
    setFilters(filterObject);
    saveFiltersToCache(TABLE_FILTERS.WALLET_TRANSACTIONS, filterObject);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    saveFiltersToCache(TABLE_FILTERS.WALLET_TRANSACTIONS, []);
    setPage(0);
    setSelectedTenant(null);
  };

  const handleSortByChange = (sortObject) => {
    setSortBy(sortObject);
    saveSortByToCache(TABLE_FILTERS.WALLET_TRANSACTIONS, sortObject);
  };

  const handleTenantChange = (event, newValue) => {
    setSelectedTenant(newValue);
    setPage(0);

    const tempColumns = [...dataTableColumns];

    const columnIndex = tempColumns?.findIndex((obj) => obj?.accessor === "user_id");

    tempColumns[columnIndex].filterOptions =
      newValue?.tenantUsers
        ?.map(({ user }) => ({
          label: `${user?.first_name || ""} ${user?.last_name}`,
          value: user?.id,
        }))
        ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    setDataTableColumns(tempColumns);
    if (!newValue) {
      setTableData((prevData) => ({
        ...prevData,
        rows: [],
      }));
      setRowCount(0);
      setTotalPages(0);
    }
  };

  const handleTabChange = (event, newValue) => {
    const newColumns = newValue === 0 ? walletTransactionColumns : cardTransactionColumns;
    const oldUserColumn = dataTableColumns?.find((col) => col?.accessor === "user_id");
    const userColumnIndex = newColumns?.findIndex((col) => col?.accessor === "user_id");

    newColumns[userColumnIndex].filterOptions = oldUserColumn?.filterOptions;

    setDataTableColumns(newColumns);
    setPage(0);
    setFilters([]);
    setSortBy([{ id: "created_at", desc: true }]);
    setActiveTab(newValue);
  };

  const getWalletTransactionData = useCallback(async () => {
    if (!selectedTenant?.value) return;

    try {
      setIsLoading(true);

      const sortId = sortBy[0]?.id;
      const sortVal = sortBy[0]?.desc ? "desc" : "asc";

      const updatedFilters = filters.map((filter) => {
        if (["transaction_type", "user_id"]?.includes(filter.id)) {
          return {
            ...filter,
            value: filter.value.value,
          };
        }
        return filter;
      });

      const filterBody = {
        page: page + 1,
        pageSize,
        filters: updatedFilters,
        sortBy: sortId ? [{ id: sortId, value: sortVal }] : [],
        tenant_name: selectedTenant?.value,
      };

      let response;
      if (activeTab === 1) {
        response = await getCardTransactions(filterBody);
      } else {
        response = await getWalletTransactions(filterBody);
      }

      if (response?.status === 200 && response.data?.data) {
        const { rows, count } = response.data.data;

        setTableData((prevData) => ({
          ...prevData,
          rows: rows || [],
        }));
        setRowCount(count || 0);
        setTotalPages(Math.ceil((count || 0) / pageSize));
      } else {
        setTableData((prevData) => ({
          ...prevData,
          rows: [],
        }));
        setNotification({
          color: "error",
          icon: "error",
          title: "Error",
          content: "Failed to fetch transactions",
          open: true,
        });
      }
    } catch (error) {
      setTableData((prevData) => ({
        ...prevData,
        rows: [],
      }));
      setNotification({
        color: "error",
        icon: "error",
        title: "Error",
        content: "Failed to fetch transactions",
        open: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, filters, sortBy, activeTab, selectedTenant]);

  useEffect(() => {
    setTableData((prevData) => ({
      ...prevData,
      columns: dataTableColumns,
    }));
  }, [dataTableColumns]);

  useEffect(() => {
    getWalletTransactionData();
  }, [getWalletTransactionData]);

  return (
    <CustomPageLayout>
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <Grid container justifyContent="space-between" spacing={3}>
              <Grid item xs={12} md={6}>
                <MDTypography variant="h5" fontWeight="medium">
                  {activeTab === 0 ? "Wallet Transactions" : "Card Transactions"}
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Here you can view all the {activeTab === 0 ? "wallet" : "card"} transactions{" "}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={4}>
                <MDAutocomplete
                  value={selectedTenant}
                  size="medium"
                  options={tenantOptions}
                  onChange={handleTenantChange}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => <TextField {...params} label="Select Tenant" />}
                  disableClearable
                />
              </Grid>
            </Grid>

            <MDBox
              mt={3}
              width={{
                sm: "100%",
                md: "50%",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    height: "35px",
                    fontWeight: "medium",
                  },
                  "& .Mui-selected": {
                    color: "primary.main",
                  },
                }}
              >
                <Tab label="Wallet Transactions" />
                <Tab label="Card Transactions" />
              </Tabs>
            </MDBox>
          </MDBox>
          <DataTable
            isLoading={isLoading}
            table={tableData}
            pageSize={pageSize}
            currentPage={page}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            filters={filters}
            handleFiltersChange={handleFiltersChange}
            sorting={sortBy}
            handleSortByChange={handleSortByChange}
            getData={getWalletTransactionData}
            handleClearAllFilters={handleClearAllFilters}
            loading={isLoading}
            rowCount={rowCount}
            key={`${activeTab}-${selectedTenant?.value}-transactions-table`}
          />
        </Card>
      </MDBox>

      <DraggableDialog
        dialogTitle="View Transaction Details"
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedTransaction(null);
        }}
        dialogContent={
          <MDBox sx={{ mt: 2 }}>
            {selectedTransaction && (
              <ViewWalletTransactionForm transactionData={selectedTransaction} />
            )}
          </MDBox>
        }
        maxWidth="sm"
      />
    </CustomPageLayout>
  );
}

export default WalletTransaction;
